import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Statistic, DatePicker, Select, Button, Space } from 'antd';
import {
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  WalletOutlined,
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { fetchFinanceStatistics, clearError } from '../../features/finance/financeSlice';
import { formatAmount, formatNumber, formatPercent } from '../../utils/index';

const { RangePicker } = DatePicker;
const { Option } = Select;

const FinanceStatistics = () => {
  const dispatch = useDispatch();
  const { statistics, isLoading, error } = useSelector((state) => state.finance);

  useEffect(() => {
    dispatch(fetchFinanceStatistics());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 资金流入流出图表
  const cashFlowOption = {
    title: {
      text: '资金流入流出趋势',
      left: 'center',
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        let result = params[0].name + '<br/>';
        params.forEach(param => {
          result += `${param.seriesName}: ${formatAmount(param.value)}<br/>`;
        });
        return result;
      },
    },
    legend: {
      data: ['充值金额', '提现金额', '净流入'],
      top: 30,
    },
    xAxis: {
      type: 'category',
      data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => formatAmount(value),
      },
    },
    series: [
      {
        name: '充值金额',
        type: 'bar',
        data: [12000, 13200, 10100, 13400, 9000, 23000, 21000],
        itemStyle: { color: '#52c41a' },
      },
      {
        name: '提现金额',
        type: 'bar',
        data: [8000, 9200, 7100, 9400, 6000, 18000, 16000],
        itemStyle: { color: '#ff4d4f' },
      },
      {
        name: '净流入',
        type: 'line',
        data: [4000, 4000, 3000, 4000, 3000, 5000, 5000],
        itemStyle: { color: '#1890ff' },
        smooth: true,
      },
    ],
  };

  // 交易类型分布图表
  const transactionTypeOption = {
    title: {
      text: '交易类型分布',
      left: 'center',
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)',
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      data: ['充值', '提现', '买入', '卖出', '返点', '奖励', '调账'],
    },
    series: [
      {
        name: '交易类型',
        type: 'pie',
        radius: '50%',
        data: [
          { value: 335, name: '充值' },
          { value: 310, name: '提现' },
          { value: 234, name: '买入' },
          { value: 135, name: '卖出' },
          { value: 100, name: '返点' },
          { value: 50, name: '奖励' },
          { value: 20, name: '调账' },
        ],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
  };

  // 成功率统计图表
  const successRateOption = {
    title: {
      text: '交易成功率统计',
      left: 'center',
    },
    tooltip: {
      trigger: 'axis',
    },
    legend: {
      data: ['充值成功率', '提现成功率', '交易成功率'],
      top: 30,
    },
    xAxis: {
      type: 'category',
      data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
    },
    yAxis: {
      type: 'value',
      max: 100,
      axisLabel: {
        formatter: '{value}%',
      },
    },
    series: [
      {
        name: '充值成功率',
        type: 'line',
        data: [95, 96, 97, 95, 98, 94, 96],
        smooth: true,
      },
      {
        name: '提现成功率',
        type: 'line',
        data: [92, 93, 94, 92, 95, 91, 93],
        smooth: true,
      },
      {
        name: '交易成功率',
        type: 'line',
        data: [98, 99, 97, 98, 99, 97, 98],
        smooth: true,
      },
    ],
  };

  // 资金池变化图表
  const fundPoolOption = {
    title: {
      text: '资金池变化趋势',
      left: 'center',
    },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        let result = params[0].name + '<br/>';
        params.forEach(param => {
          result += `${param.seriesName}: ${formatAmount(param.value)}<br/>`;
        });
        return result;
      },
    },
    legend: {
      data: ['总资金池', '可用资金', '冻结资金'],
      top: 30,
    },
    xAxis: {
      type: 'category',
      data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => formatAmount(value),
      },
    },
    series: [
      {
        name: '总资金池',
        type: 'line',
        data: [1000000, 1020000, 1010000, 1030000, 1040000, 1050000, 1060000],
        smooth: true,
      },
      {
        name: '可用资金',
        type: 'line',
        data: [800000, 820000, 810000, 830000, 840000, 850000, 860000],
        smooth: true,
      },
      {
        name: '冻结资金',
        type: 'line',
        data: [200000, 200000, 200000, 200000, 200000, 200000, 200000],
        smooth: true,
      },
    ],
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">财务统计</h1>
        <p className="page-description">财务数据统计和分析</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总充值金额"
              value={statistics.totalRecharge}
              prefix={<RiseOutlined />}
              valueStyle={{ color: '#52c41a' }}
              formatter={(value) => formatAmount(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总提现金额"
              value={statistics.totalWithdraw}
              prefix={<FallOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
              formatter={(value) => formatAmount(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="净流入"
              value={statistics.netInflow}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
              formatter={(value) => formatAmount(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="总交易金额"
              value={statistics.totalTransactions}
              prefix={<WalletOutlined />}
              valueStyle={{ color: '#722ed1' }}
              formatter={(value) => formatAmount(value)}
            />
          </Card>
        </Col>
      </Row>

      {/* 待处理统计 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="待处理充值"
              value={statistics.pendingRecharge}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
              formatter={(value) => formatAmount(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="待处理提现"
              value={statistics.pendingWithdraw}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
              formatter={(value) => formatAmount(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="交易成功率"
              value={statistics.successRate}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
              formatter={(value) => formatPercent(value)}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="平均交易金额"
              value={statistics.averageTransactionAmount}
              prefix={<BankOutlined />}
              valueStyle={{ color: '#1890ff' }}
              formatter={(value) => formatAmount(value)}
            />
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="资金流入流出趋势" style={{ height: 400 }}>
            <ReactECharts
              option={cashFlowOption}
              style={{ height: '300px' }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="交易类型分布" style={{ height: 400 }}>
            <ReactECharts
              option={transactionTypeOption}
              style={{ height: '300px' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="交易成功率统计" style={{ height: 400 }}>
            <ReactECharts
              option={successRateOption}
              style={{ height: '300px' }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="资金池变化趋势" style={{ height: 400 }}>
            <ReactECharts
              option={fundPoolOption}
              style={{ height: '300px' }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FinanceStatistics;
